import { find } from 'lodash';
import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult, useQuery } from 'react-query';

import { shopsApi, tradersApi } from 'api';
import {
  CopyText,
  DataGridColumnDefinition,
  PaginationWrapper,
  DateLabel,
  QueryFilters,
  CopyTextId,
} from 'components';
import { DateFormat, FilterDefinitionType, QueryKey } from 'enums';
import { OrderAutomationListWithAction } from 'features/order-automation';
import {
  useCurrencies,
  useQueryFilters,
  useShopsQuery,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  OrderAutomation,
  OrderAutomationFilters,
  PaginatedData,
} from 'types';
import { formatUtils, requisitesUtils, formUtils, filtersUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PaginatedData<OrderAutomation>, unknown>;
  additionalColumns?: DataGridColumnDefinition<OrderAutomation>[];
  additionalFilters?: FilterDefinition<OrderAutomationFilters>[];
};

export const ManageOrderAutomationList: React.FC<Props> = ({
  queryResult,
  additionalColumns = [],
  additionalFilters = [],
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.order_automation',
  });

  const { paymentTypes, banks } = useUserContext();
  const { role, isManager } = useUser();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
    {
      enabled: isManager,
    },
  );

  const queryResultTraders = useQuery(QueryKey.Traders, () =>
    tradersApi.getAllAsRole(role)(),
  );

  const { filters } = useQueryFilters<OrderAutomationFilters>();

  const [selectedOrderAutomation, setSelectedOrderAutomation] =
    useState<OrderAutomation | null>(null);

  const columns = useMemo(
    (): DataGridColumnDefinition<OrderAutomation>[] => [
      {
        header: t('table.columns.id'),
        valueGetter: (item) => (
          <div>
            <CopyTextId id={item.id} />
            <DateLabel>
              {formatUtils.formatDate(item.createdAt, DateFormat.Full)}
            </DateLabel>
          </div>
        ),
      },
      {
        header: t('table.columns.text'),
        valueGetter: (item) => <CopyText text={item.text} />,
      },
      {
        header: t('table.columns.order_automation_type'),
        valueGetter: (item) => {
          if (item.requisitesGroup) {
            return `${requisitesUtils.getOrderAutomationTypeLabel(
              item.orderAutomationType,
            )} (${t('group')})`;
          }

          if (item.requisites) {
            return requisitesUtils.getOrderAutomationTypeLabel(
              item.orderAutomationType,
            );
          }

          return '';
        },
      },
      ...additionalColumns,
    ],
    [t, additionalColumns],
  );

  const filtersDefinitions: FilterDefinition<OrderAutomationFilters>[] =
    useMemo(
      () => [
        {
          label: t('filters.automation_id'),
          name: 'orderAutomationId',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
        {
          label: t('filters.order_id'),
          name: 'orderId',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
        {
          label: t('filters.requisites_group_id'),
          name: 'requisitesGroupId',
          type: FilterDefinitionType.Text,
          format: 'uuid',
        },
        {
          label: t('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
          hidden: !isManager,
        },
        {
          label: t('filters.trader'),
          name: 'traderId',
          type: FilterDefinitionType.Trader,
          traders: queryResultTraders.data,
          hidden: !isManager,
        },
        {
          label: t('filters.payment_type'),
          name: 'paymentTypeId',
          type: FilterDefinitionType.Select,
          options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
          getDisplayName: (value: string) =>
            requisitesUtils.getPaymentTypeLabel(
              find(paymentTypes, { id: value })!,
            ),
        },
        {
          label: t('filters.bank'),
          name: 'bankId',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(banks),
          getDisplayName: (value: string) => find(banks, { id: value })?.name,
        },
        {
          label: t('filters.fiat_currency'),
          name: 'fiatCurrencyId',
          type: FilterDefinitionType.Select,
          options: fiatCurrenciesOptions,
          getDisplayName: getFiatCurrencyCode,
        },
        {
          label: t('filters.text'),
          name: 'text',
          type: FilterDefinitionType.Text,
        },
        ...requisitesUtils.getRequisitesFieldsFilters<OrderAutomationFilters>(),
      ],
      [
        banks,
        fiatCurrenciesOptions,
        getFiatCurrencyCode,
        isManager,
        paymentTypes,
        queryResultShops.data,
        queryResultTraders.data,
        t,
      ],
    );

  return (
    <Fragment>
      <QueryFilters
        filtersDefinitions={[
          ...filtersUtils.getCommonFilters(
            filters,
            FilterDefinitionType.DateTimeRange,
          ),
          ...filtersDefinitions,
          ...additionalFilters,
        ]}
      />
      <PaginationWrapper
        queryResult={queryResult}
        ignoreState={{ refetchLoading: true }}
      >
        <OrderAutomationListWithAction
          queryResult={queryResult}
          selectedOrderAutomation={selectedOrderAutomation}
          handleOrderAutomationSelection={setSelectedOrderAutomation}
          columns={columns}
        />
      </PaginationWrapper>
    </Fragment>
  );
};
